import { Box, Paper, Stack, Typography } from '@mui/material';
import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Team } from '../../types/domain';
import { useAutoRotate } from '../../hooks/useAutoRotate';
import { MEDAL_COLORS, buildBoardRows, type BoardRow } from './BoardShared';
import { buildBoardSlots, type BoardSlot } from './boardSlots';

interface FocusBoardProps {
  competition: CompetitionPublic;
  results: Result[];
  overall: OverallRanking[];
  allTeams: Team[] | undefined;
}

export const FocusBoard = ({ competition, results, overall, allTeams }: FocusBoardProps) => {
  const slots = buildBoardSlots(competition, results, overall);
  const { index } = useAutoRotate({
    total: slots.length,
    intervalMs: Math.max(5, competition.publicRotationSeconds ?? 10) * 1000,
  });
  const active = slots[index];

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {active && (
        <Paper
          elevation={3}
          sx={{
            flex: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            background: active.kind === 'OVERALL'
              ? 'linear-gradient(135deg, #C62828 0%, #B71C1C 100%)'
              : 'linear-gradient(135deg, #FFB300 0%, #FF8F00 100%)',
            color: '#FFFFFF',
            minHeight: 0,
          }}
        >
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, letterSpacing: '0.15em', opacity: 0.85, color: 'inherit' }}
          >
            {active.category.name}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 900, lineHeight: 1.1, mb: 2, color: 'inherit' }}
          >
            {active.kind === 'DISCIPLINE' ? active.discipline.name : 'Celkové pořadí'}
          </Typography>
          {active.kind === 'DISCIPLINE' ? (
            <DisciplineRows
              results={active.results}
              categoryId={active.category.id}
              disciplineId={active.discipline.id}
              allTeams={allTeams}
              showAll={!!competition.boardShowAllTeams}
              sortMode={competition.boardSortMode}
            />
          ) : (
            <OverallRows ranking={active.ranking} />
          )}
        </Paper>
      )}

      {/* Malé karty ostatních slotu */}
      <Box
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: 'minmax(180px, 1fr)',
          gap: 1.5,
          overflowX: 'auto',
        }}
      >
        {slots.map((s, i) => (
          <MiniCard key={s.key} slot={s} active={i === index} />
        ))}
      </Box>
    </Box>
  );
};

const DisciplineRows = ({
  results,
  categoryId,
  disciplineId,
  allTeams,
  showAll,
  sortMode,
}: {
  results: Result[];
  categoryId: number;
  disciplineId: number;
  allTeams: Team[] | undefined;
  showAll: boolean;
  sortMode: CompetitionPublic['boardSortMode'];
}) => {
  const rows: BoardRow[] = buildBoardRows(results, allTeams, categoryId, disciplineId, {
    showAll,
    sortMode,
    topN: 10,
  });
  if (rows.length === 0) {
    return (
      <Typography variant="h5" sx={{ opacity: 0.75 }}>
        zatím žádné výsledky
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75} sx={{ flex: 1 }}>
      {rows.map((r) => {
        const medal = r.rank != null ? MEDAL_COLORS[r.rank] : null;
        return (
          <Box
            key={r.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 80px 1fr auto',
              alignItems: 'center',
              gap: 2,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: medal ? 'rgba(255,255,255,0.18)' : 'transparent',
              borderLeft: medal ? `6px solid ${medal.bg}` : '6px solid rgba(255,255,255,0.1)',
              opacity: r.__missing ? 0.55 : 1,
            }}
          >
            <RankBadgeBoard rank={r.rank} medal={medal} />
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', opacity: 0.8 }}>
              #{r.teamStartNumber}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.75rem', letterSpacing: '-0.02em' }}>
              {r.isNp ? 'NP' : r.timeFinalFormatted}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const OverallRows = ({ ranking }: { ranking: OverallRanking }) => {
  const top = [...ranking.teams].sort((a, b) => a.finalRank - b.finalRank).slice(0, 10);
  return (
    <Stack spacing={0.75} sx={{ flex: 1 }}>
      {top.map((t) => {
        const medal = MEDAL_COLORS[t.finalRank];
        return (
          <Box
            key={t.teamId}
            sx={{
              display: 'grid',
              gridTemplateColumns: '56px 80px 1fr auto',
              alignItems: 'center',
              gap: 2,
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              bgcolor: medal ? 'rgba(255,255,255,0.18)' : 'transparent',
              borderLeft: medal ? `6px solid ${medal.bg}` : '6px solid rgba(255,255,255,0.1)',
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: '2rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {t.finalRank}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', opacity: 0.8 }}>
              #{t.startNumber}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.teamName}
            </Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.75rem' }}>
              {t.totalPoints} b.
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

const RankBadgeBoard = ({ rank, medal }: { rank: number | null; medal: { bg: string; fg: string } | null }) => {
  const bg = medal ? medal.bg : 'rgba(255,255,255,0.2)';
  const fg = medal ? medal.fg : '#FFFFFF';
  const label = rank ?? '–';
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '3rem',
        height: '3rem',
        borderRadius: '999px',
        bgcolor: bg,
        color: fg,
        fontWeight: 900,
        fontSize: '1.5rem',
        fontVariantNumeric: 'tabular-nums',
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
  );
};

const MiniCard = ({ slot, active }: { slot: BoardSlot; active: boolean }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.25,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.selected' : 'background.paper',
      minHeight: 72,
    }}
  >
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'primary.main' }}
    >
      {slot.category.name}
    </Typography>
    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1 }}>
      {slot.kind === 'DISCIPLINE' ? slot.discipline.name : 'Celkové pořadí'}
    </Typography>
  </Paper>
);
